import { type AppErrorCode, isAppErrorCode } from "@bt/domain";
import { TRPCClientError } from "@trpc/client";
import type { TFunction } from "i18next";

/**
 * The error answer of an API route outside tRPC (uploads, imports): its JSON carries the code,
 * as a tRPC error's data does.
 */
export class ApiError extends Error {
  readonly appCode: AppErrorCode | null;

  constructor(appCode: unknown) {
    super(typeof appCode === "string" ? appCode : "API error");
    this.name = "ApiError";
    this.appCode = isAppErrorCode(appCode) ? appCode : null;
  }

  /** The error a failed response answered with. */
  static async from(response: Response): Promise<ApiError> {
    const body = (await response.json().catch(() => ({}))) as { appCode?: unknown };
    return new ApiError(body.appCode);
  }
}

export function errorCode(error: unknown): AppErrorCode | null {
  if (error instanceof TRPCClientError) {
    const code = (error.data as { appCode?: unknown } | undefined)?.appCode;
    if (isAppErrorCode(code)) return code;
  }
  if (error instanceof ApiError) return error.appCode;
  return null;
}

/** User-facing message for any error from the API, in the current language. */
export function errorMessage(error: unknown, t: TFunction): string {
  const code = errorCode(error);
  if (code) return t(`errors.${code}`);
  if (isConnectionError(error)) return t("errors.network");
  return t("errors.generic");
}

/** The request never got an answer: no connection, or the server out of reach. */
export function isConnectionError(error: unknown): boolean {
  if (error instanceof TRPCClientError) {
    return !error.data && (error.cause instanceof TypeError || !navigator.onLine);
  }
  return error instanceof TypeError && /fetch|network|load failed/i.test(error.message);
}
